// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders / Rate & Review  (route /orders/:ref/review — "Rate & Review Catch")
// A customer reviews a DELIVERED order, once: an overall star rating with a mood line and quick ticks (fresh, well cleaned…),
// then each item — stars, how well it was cleaned / prepared, whether it was cut as ordered — then the delivery (stars +
// ticks: on time, polite, packaging intact… or late, packaging damaged), and optional notes (500 characters). Only the stars
// for the overall rating are required; anything left untouched is simply not sent. POST /api/me/orders/:ref/review; the
// server allows one review per delivered order. Once reviewed the page shows the review read-only.
// A low rating (≤ 2) points to Contact so a real problem reaches ZenFresh, not just a star count.
// Deliberately NOT here (nothing supports it): a named courier with delivery statistics, thermal-box / cold-chain
// readings, photo upload, "Catch Credits" / wallet rewards for reviewing (an incentive ZenFresh hasn't decided on), badges.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { packById } from "../data/packs";
import { seafoodItems } from "../data/seafood";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { DELIVERY_TAGS, MOODS, PRAISE_TAGS, fetchOrder, formatWhen, reachedAt, submitReview, type Order, type ReviewInput } from "../utils/orders";

type Load = { status: "loading" } | { status: "missing" } | { status: "error" } | { status: "ready"; order: Order };
type Cleaning = "excellent" | "good" | "needs_work";

const CLEANING: { id: Cleaning; label: string }[] = [
  { id: "excellent", label: "Excellent" },
  { id: "good", label: "Good" },
  { id: "needs_work", label: "Needs work" },
];

/** Five tappable stars as a radio group (keyboard: arrow keys move between the radios). */
function Stars({ value, onChange, label, size = "h-9 w-9" }: { value: number; onChange?: (n: number) => void; label: string; size?: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} ${n === 1 ? "star" : "stars"}`}
          disabled={!onChange}
          onClick={() => onChange?.(n)}
          className="rounded-full p-0.5 text-coral-500 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:hover:scale-100"
        >
          <Star className={`${size} ${n <= value ? "fill-current" : "opacity-40"}`} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

const chipClass = (on: boolean) =>
  `inline-flex min-h-11 items-center rounded-full px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
    on ? "bg-ocean-800 text-white" : "bg-ocean-700/10 text-ocean-900 hover:bg-ocean-700/15 dark:bg-white/10 dark:text-foam-50"
  }`;

export default function OrderReview() {
  const { ref = "" } = useParams();
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  useDocumentTitle("Rate & review");

  useEffect(() => {
    if (!signedIn) return;
    fetchOrder(ref).then(
      (order) => setLoad({ status: "ready", order }),
      (error) => setLoad({ status: error instanceof ApiError && error.status === 404 ? "missing" : "error" }),
    );
  }, [signedIn, ref]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  if (load.status !== "ready") {
    return (
      <PageTransition>
        <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
          <div className="mx-auto max-w-xl px-4">
            <p role={load.status === "loading" ? "status" : "alert"} className="rounded-2xl bg-white p-8 text-center text-sm shadow-sm dark:bg-ocean-900">
              {load.status === "loading" ? "Loading your order…" : load.status === "missing" ? "We couldn't find that order." : "We couldn't load this order. Check your connection and try again."}{" "}
              <Link to="/orders" className="font-semibold underline">My orders</Link>
            </p>
          </div>
        </div>
      </PageTransition>
    );
  }
  return <ReviewForm order={load.order} onSaved={(order) => setLoad({ status: "ready", order })} />;
}

function ReviewForm({ order, onSaved }: { order: Order; onSaved: (order: Order) => void }) {
  const navigate = useNavigate();
  const [overall, setOverall] = useState(0);
  const [praise, setPraise] = useState<string[]>([]);
  const [items, setItems] = useState<Record<string, { rating: number; cleaning?: Cleaning; cutAsOrdered?: boolean }>>({});
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [deliveryTags, setDeliveryTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const delivered = order.status === "delivered";
  const done = order.review;
  const readOnly = done !== null;
  // One entry per catch (an order can hold two pack sizes of the same catch; they are reviewed together).
  const catches = order.items.filter((line, i, all) => all.findIndex((other) => other.id === line.id) === i);
  const toggle = (list: string[], set: (next: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const setItem = (id: string, change: Partial<(typeof items)[string]>) => setItems((current) => ({ ...current, [id]: { ...current[id], rating: current[id]?.rating ?? 0, ...change } }));
  const deliveredAt = reachedAt(order, "delivered");

  const submit = async () => {
    setBusy(true);
    setError(null);
    const input: ReviewInput = {
      overall,
      praise,
      items: Object.entries(items)
        .filter(([, v]) => v.rating > 0)
        .map(([id, v]) => ({ id, rating: v.rating, ...(v.cleaning ? { cleaning: v.cleaning } : {}), ...(v.cutAsOrdered !== undefined ? { cutAsOrdered: v.cutAsOrdered } : {}) })),
      ...(deliveryRating > 0 ? { delivery: { rating: deliveryRating, tags: deliveryTags } } : {}),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    };
    try {
      onSaved(await submitReview(order.ref, input));
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "REVIEW_ALREADY_EXISTS"
          ? "You've already reviewed this order."
          : e instanceof ApiError && e.code === "REVIEW_NOT_ALLOWED"
            ? "You can review an order once it has been delivered."
            : "We couldn't save your review. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const shownOverall = done ? done.overall : overall;
  const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-40 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs dynamicLabel={order.ref} />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to={`/orders/${order.ref}`} aria-label="Back to the order" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Rate &amp; Review Catch</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75"><span className="font-mono">{order.ref}</span>{deliveredAt ? ` • Delivered ${formatWhen(deliveredAt)}` : ""}</p>
            </div>
          </header>

          {!delivered && (
            <p role="status" className="rounded-2xl bg-white p-6 text-center text-sm shadow-sm dark:bg-ocean-900">
              You can rate this order once it has been delivered. <Link to={`/orders/${order.ref}`} className="font-semibold underline">Back to the order</Link>
            </p>
          )}

          {delivered && readOnly && (
            <p role="status" className="rounded-xl bg-teal-100 px-4 py-2 text-sm font-semibold text-teal-700">
              Thanks — you reviewed this order on {formatWhen(done.createdAt)}. Reviews can&apos;t be changed.
            </p>
          )}

          {delivered && (
            <>
              <section aria-labelledby="overall-heading" className={`${card} text-center`}>
                <h2 id="overall-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">How fresh was your catch?</h2>
                <p aria-live="polite" className="mt-1 text-sm font-semibold text-teal-700 dark:text-teal-300">{shownOverall > 0 ? MOODS[shownOverall - 1] : "Tap a star to rate"}</p>
                <div className="mt-2 flex justify-center"><Stars value={shownOverall} onChange={readOnly ? undefined : setOverall} label="Overall rating" /></div>
                <div role="group" aria-label="What was good" className="mt-3 flex flex-wrap justify-center gap-2">
                  {PRAISE_TAGS.map((tag) => {
                    const on = (done ? done.praise : praise).includes(tag.id);
                    return (
                      <button key={tag.id} type="button" aria-pressed={on} disabled={readOnly} onClick={() => toggle(praise, setPraise, tag.id)} className={chipClass(on)}>
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
                {!readOnly && overall > 0 && overall <= 2 && (
                  <p className="mt-3 rounded-lg bg-coral-400/10 px-3 py-2 text-xs font-semibold text-coral-500 dark:text-coral-300">
                    We&apos;re sorry it wasn&apos;t right. Please also <Link to={`/contact?subject=${encodeURIComponent(`Problem with order ${order.ref}`)}`} className="underline">tell us what went wrong</Link> so we can put it right.
                  </p>
                )}
              </section>

              <section aria-labelledby="items-heading" className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between px-1">
                  <h2 id="items-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Item quality &amp; cutting</h2>
                  <span className="text-xs font-bold text-teal-700 dark:text-teal-300">{catches.length} {catches.length === 1 ? "item" : "items"} · optional</span>
                </div>
                {catches.map((line) => {
                  const item = seafoodItems.find((candidate) => candidate.id === line.id);
                  const saved = done?.items.find((i) => i.id === line.id);
                  const value = saved ? { rating: saved.rating, cleaning: saved.cleaning ?? undefined, cutAsOrdered: saved.cutAsOrdered ?? undefined } : items[line.id] ?? { rating: 0 };
                  return (
                    <div key={line.id} className={`${card} flex flex-col gap-3`}>
                      <div className="flex gap-3">
                        {item ? <img src={item.image} alt="" width={64} height={64} loading="lazy" className="h-16 w-16 shrink-0 rounded-xl object-cover" /> : <span aria-hidden="true" className="h-16 w-16 shrink-0 rounded-xl bg-ocean-700/10" />}
                        <div className="min-w-0">
                          <p className="font-semibold leading-snug text-ocean-950 dark:text-foam-50">{line.name}</p>
                          <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{packById(line.pack).label} · {line.cut}</p>
                          <div className="mt-1"><Stars value={value.rating} onChange={readOnly ? undefined : (n) => setItem(line.id, { rating: n })} label={`Rating for ${line.name}`} size="h-7 w-7" /></div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-ocean-800/80 dark:text-foam-100/75" id={`clean-${line.id}`}>Cleaning &amp; preparation</p>
                        <div role="group" aria-labelledby={`clean-${line.id}`} className="mt-1 grid grid-cols-3 gap-2">
                          {CLEANING.map((c) => (
                            <button key={c.id} type="button" aria-pressed={value.cleaning === c.id} disabled={readOnly} onClick={() => setItem(line.id, { cleaning: value.cleaning === c.id ? undefined : c.id })} className={chipClass(value.cleaning === c.id)}>
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-ocean-800/80 dark:text-foam-100/75" id={`cut-${line.id}`}>Was it cut as you ordered?</p>
                        <div role="group" aria-labelledby={`cut-${line.id}`} className="mt-1 grid grid-cols-2 gap-2">
                          <button type="button" aria-pressed={value.cutAsOrdered === true} disabled={readOnly} onClick={() => setItem(line.id, { cutAsOrdered: value.cutAsOrdered === true ? undefined : true })} className={chipClass(value.cutAsOrdered === true)}>Yes, as ordered</button>
                          <button type="button" aria-pressed={value.cutAsOrdered === false} disabled={readOnly} onClick={() => setItem(line.id, { cutAsOrdered: value.cutAsOrdered === false ? undefined : false })} className={chipClass(value.cutAsOrdered === false)}>Not quite</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>

              <section aria-labelledby="delivery-heading" className={card}>
                <div className="flex items-baseline justify-between">
                  <h2 id="delivery-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Delivery</h2>
                  <span className="text-xs font-bold text-teal-700 dark:text-teal-300">optional</span>
                </div>
                <div className="mt-2"><Stars value={done?.delivery?.rating ?? deliveryRating} onChange={readOnly ? undefined : setDeliveryRating} label="Delivery rating" size="h-8 w-8" /></div>
                <div role="group" aria-label="About the delivery" className="mt-3 flex flex-wrap gap-2">
                  {DELIVERY_TAGS.map((tag) => {
                    const on = (done?.delivery?.tags ?? deliveryTags).includes(tag.id);
                    return (
                      <button key={tag.id} type="button" aria-pressed={on} disabled={readOnly} onClick={() => toggle(deliveryTags, setDeliveryTags, tag.id)} className={chipClass(on)}>
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section aria-labelledby="notes-heading" className={card}>
                <div className="flex items-baseline justify-between">
                  <label id="notes-heading" htmlFor="review-notes" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Catch notes &amp; recipe comments</label>
                  <span className="text-xs font-semibold text-ocean-800/70 dark:text-foam-100/65">Optional</span>
                </div>
                <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">How did you cook it? Any tips for other fish lovers?</p>
                <textarea
                  id="review-notes"
                  rows={4}
                  maxLength={500}
                  value={done ? (done.comment ?? "") : comment}
                  readOnly={readOnly}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Fried the pomfret with a mild rava crust — so fresh, no muddy taste…"
                  className="mt-2 w-full resize-none rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-3 text-ocean-950 outline-none focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                />
                {!readOnly && <p className="mt-1 text-right text-xs text-ocean-800/70 dark:text-foam-100/65">{comment.length}/500</p>}
              </section>

              {error && <p role="alert" className="text-center text-sm font-semibold text-coral-500 dark:text-coral-300">{error}</p>}
            </>
          )}
        </div>

        {delivered && !readOnly && (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ocean-900/10 bg-white px-4 pb-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-ocean-900">
            <div className="mx-auto flex max-w-xl flex-col gap-1 pr-16 sm:pr-0">
              <button type="button" onClick={() => void submit()} disabled={overall === 0 || busy} className="min-h-12 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:opacity-50">
                {busy ? "Saving…" : overall === 0 ? "Pick a star rating to submit" : "Submit review"}
              </button>
              <button type="button" onClick={() => navigate(`/orders/${order.ref}`)} className="min-h-11 text-sm font-semibold text-ocean-800/80 hover:text-ocean-900 dark:text-foam-100/75">
                Not now
              </button>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
}
